"use client";

import * as Accordion from "@radix-ui/react-accordion";
import clsx from "clsx";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { useCallback } from "react";

import type { FragmentType } from "../../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../../__generated__/gql";
import { FacilityDetails } from "../../_components/facility-details";
import { useDotButton } from "../../_hooks/use-dot-button";

const Fragment = graphql(`
  fragment ContentArchitectureTypeVariant on ContentArchitectureTypeVariant {
    name
    brief
    imageUrls {
      rawUrl
    }
    description
    facilityDetails {
      ...ContentFacilityDetails
    }
    sites {
      ...AccommodationsPerSite
    }
  }
`);

export function ArchitectureTypeVariantAccordion({
  variantFragment,
  className,
}: {
  variantFragment: FragmentType<typeof Fragment>;
  className?: string;
}) {
  const variant = useFragment(Fragment, variantFragment);
  const isCarouselActive = variant.imageUrls.length > 1;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    active: isCarouselActive,
  });
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);
  return (
    <Accordion.Item value={variant.name}>
      <Accordion.Header className="flex w-full justify-center">
        <Accordion.Trigger
          className={clsx(
            "bg-sbeige font-prophet h-[48px] px-[20px] text-[18px] md:h-[80px] md:px-[40px] md:text-[24px]",
            "group flex w-full items-center justify-between",
            className,
          )}
        >
          <div className="text-[14px] group-data-[state=open]:font-bold">
            {variant.name}
          </div>
          <Image
            src="/i/icon_chevron_down.svg"
            className="transition-transform duration-300 group-data-[state=open]:rotate-180"
            width={18}
            height={18}
            alt="down"
          />
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content className="sa-content data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <div className="flex w-[calc(100%+80px)] translate-x-[-40px] gap-[10px] overflow-hidden">
          {/* 
            コンテンツ全体(100%)に対し、カルーセルのボタン分の余白（左右40pxずつ = 80px）を作るために：
            - 幅100%+80pxとする
            - 40px左にずらす
          */}
          <div className="flex max-h-[480px] w-[30px] items-center justify-center">
            <button
              // カルーセルの送りだけで、行動の分析対象にならない
              data-analytics-ignore
              className={clsx("h-[30px] w-[30px]", {
                hidden: !isCarouselActive,
              })}
              onClick={scrollPrev}
            >
              <Image
                src="/i/icon_chevron_left.svg"
                alt="前の画像へ"
                width={30}
                height={30}
              />
            </button>
          </div>
          <div className="w-[calc(100%-80px)] overflow-hidden">
            <div ref={emblaRef}>
              <div className="flex">
                {variant.imageUrls.map((imageUrl, i) => (
                  <div
                    key={i}
                    className="bg-slightbeige-108 aspect-33/20 max-h-[480px] w-full min-w-0 flex-[0_0_100%]"
                  >
                    <Image
                      src={imageUrl.rawUrl}
                      alt="slide"
                      sizes="100vw"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                      width={33}
                      height={20}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex max-h-[480px] w-[30px] items-center justify-center">
            <button
              // カルーセルの送りだけで、行動の分析対象にならない
              data-analytics-ignore
              className={clsx("h-[30px] w-[30px]", {
                hidden: !isCarouselActive,
              })}
              onClick={scrollNext}
            >
              <Image
                src="/i/icon_chevron_left.svg"
                alt="次の画像へ"
                width={30}
                height={30}
                className="rotate-180"
              />
            </button>
          </div>
        </div>
        <div className="bg-slightbeige-108 flex flex-col gap-[10px] p-[10px_20px_20px] md:gap-[20px] md:p-[20px_40px_40px]">
          <div
            className={clsx("flex justify-center gap-[10px]", {
              hidden: !isCarouselActive,
            })}
          >
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                // カルーセルの送りだけで、行動の分析対象にならない
                data-analytics-ignore
                onClick={() => onDotButtonClick(index)}
                className={clsx(
                  "bg-sbeige h-[5px] w-[5px] rounded-full",
                  selectedIndex === index && "bg-skogecha",
                )}
              ></button>
            ))}
          </div>
          <div className="text-[12px] md:pb-[10px] md:text-[16px]">
            <p>{variant.description}</p>
          </div>
          <FacilityDetails facilityDetailsFragment={variant.facilityDetails} />
          {/* <div className="grid grid-cols-1 gap-y-[10px] pt-[10px] text-[10px] md:grid-cols-3 md:gap-y-[20px] md:pt-0 md:text-[13px]">
            <h3>
              <b>対象拠点</b>
            </h3>
            <div className="col-span-2 flex flex-col gap-[10px] text-[10px] md:text-[12px]">
              {variant.sites.map((site, i) => (
                <AccommodationsPerSite key={i} siteFragment={site} />
              ))}
            </div>
          </div> */}
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
}
